import * as React from 'react'
import Box from '@mui/material/Box'
import { Button, TextField } from '@mui/material'
import styled from '@emotion/styled'
import { Cached, Search } from '@mui/icons-material'
import { useQueryState } from 'react-router-use-location-state'
import { useFaqQuery } from 'services/info-center/faq'
import { IInfoCenterFaq } from 'types/info-center/faq'
import { RegulationAccordion } from 'components/purchase/RegulationAccordion'
import { CustomPagination } from 'components/purchase/CustomPagination'
import { useForm } from 'react-hook-form'
import { T } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

export interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const StyledButton = styled(Button)({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: '4px',
  minWidth: '48px',
  height: '48px',
  padding: '14px',
  textDecoration: 'none',
  transition: 'all 0.4s ease 0s',
  '& svg': {
    color: '#fff',
    fontSize: '20px',
  },
})

export const FaqAccordion: React.FC = () => {
  const { t } = useTranslation()
  const [page, setPage] = useQueryState('page', 0)
  const [keyword, setKeyword] = useQueryState('keyword', '')
  const rowsPerPage = 10
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<{ keyword: string }>({
    defaultValues: {
      keyword,
    },
  })

  const onSubmit = (values: { keyword: string }) => {
    setKeyword(values.keyword)
  }

  const handleReset = () => {
    reset()
    setKeyword('')
  }

  const args = {
    page,
    size: rowsPerPage,
    keyword,
  } as const

  const { data, isError, isLoading } = useFaqQuery(args, {
    refetchOnMountOrArgChange: true,
  })

  return (
    <Box>
      <form id="faq-filter-form" onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" sx={{ margin: '24px 0' }}>
          <TextField
            InputLabelProps={{ shrink: false }}
            id="search"
            placeholder={t('faq-search')}
            variant="outlined"
            fullWidth
            sx={{
              maxHeight: '48px',
              '& .MuiInputBase-input': {
                borderTopRightRadius: '0 !important',
                borderBottomRightRadius: '0 !important',
              },
              '& fieldset': {
                borderTopRightRadius: '0 !important',
                borderBottomRightRadius: '0 !important',
              },
            }}
            {...register('keyword')}
          />
          <Box display="flex" gap="10px">
            <StyledButton
              sx={{
                background: 'rgb(122, 127, 126)',
                borderTopLeftRadius: '0 !important',
                borderBottomLeftRadius: '0 !important',
                '&:hover': {
                  background: 'rgb(135, 141, 140)',
                },
              }}
              type="submit"
              form="faq-filter-form"
            >
              <Search />
            </StyledButton>
            <StyledButton
              sx={{
                background: 'rgb(3, 34, 76)',
                color: '#fff',
                '&:hover': {
                  background: 'rgba(3, 34, 76, 0.6)',
                },
              }}
              onClick={handleReset}
            >
              <Cached />
            </StyledButton>
          </Box>
        </Box>
      </form>

      {data?.items.map((faq: IInfoCenterFaq) => {
        return (
          <RegulationAccordion
            key={faq.id}
            title={faq.question}
            content={faq.answer}
          />
        )
      })}
      {data?.info.totalRows !== 0 && (
        <CustomPagination
          total={data?.info.totalRows!}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={setPage}
        />
      )}
    </Box>
  )
}
